"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const three_1 = require("three");
// eslint-disable-next-line import/no-cycle
const types_1 = require("~/store/types");
const events_1 = require("~/consts/events");
const Cad3d_scss_1 = __importDefault(require("../Cad3d.scss"));
const DimensioningLeader_3d_1 = __importDefault(require("./DimensioningLeader.3d"));
const consts_1 = require("../consts");
// eslint-disable-next-line import/no-cycle
const utils_1 = require("../utils");
// eslint-disable-next-line import/no-cycle
const utils_3d_1 = require("../utils/utils.3d");
const consts_2 = require("../consts/consts");
const createNearestDistance = () => ({ minDist: Infinity, line: null });
class DimensioningTool {
    get currentObject() {
        return this.mainTool.transformControlsInitial.object || this.snappingTool.getObject();
    }
    constructor(viewer, mainTool, snappingTool) {
        this.toEndsLeaders = [];
        this.neighborLeaders = [];
        this.panelLeaders = [];
        this.guidelines = [];
        this.overlayLines = [];
        this.positioningLeaders = [];
        this.positioningLeaderIndex = 0;
        this.positioningIsSnapped = false;
        this.viewer = viewer;
        this.mainTool = mainTool;
        this.snappingTool = snappingTool;
        this.lineMaterial = this.createLineMaterial();
    }
    dispose() {
        var _a;
        this.positioningLeaders = [];
        this.hidePositioningInput();
        if (this.toEndsLeaders && this.toEndsLeaders.length > 0) {
            this.toEndsLeaders.forEach((leader) => leader.clear());
            this.toEndsLeaders = [];
        }
        if (this.neighborLeaders && this.neighborLeaders.length > 0) {
            this.neighborLeaders.forEach((leader) => leader.clear());
            this.neighborLeaders = [];
        }
        if (this.panelLeaders && this.panelLeaders.length > 0) {
            this.panelLeaders.forEach((leader) => leader.clear());
            this.panelLeaders = [];
        }
        if (this.guidelines && this.guidelines.length > 0) {
            this.guidelines.forEach((guideline) => guideline.removeFromParent());
            this.guidelines = [];
        }
        if ((_a = this.overlayLines) === null || _a === void 0 ? void 0 : _a.length) {
            this.overlayLines.forEach((l) => l.removeFromParent());
            this.overlayLines = [];
        }
    }
    displayDistances(isSnapped) {
        this.positioningIsSnapped = isSnapped;
        this.drawDistances(isSnapped);
    }
    refreshPositioningDistances(activeLeaderKey) {
        this.drawDistances(this.positioningIsSnapped, activeLeaderKey, true);
    }
    drawDistances(isSnapped, activeLeaderKey, preservePositioningInput = false) {
        this.dispose();
        if (!this.currentObject ||
            utils_1.Utils.isCoverComponent(this.currentObject) ||
            !this.currentObject.visible ||
            this.currentObject.snappedToAngledExtrusion) {
            return;
        }
        if (isSnapped) {
            const snappedObject = this.getSnappedObject();
            if (!snappedObject) {
                return;
            }
            if (snappedObject.type === types_1.ITEM_TYPE.EXTRUSION) {
                const dimensions = this.calculateDistancesToSnappedObjectEdges(snappedObject);
                if (dimensions === null || dimensions === void 0 ? void 0 : dimensions.length) {
                    this.drawDistancesToSnappedObjectEdges(dimensions, activeLeaderKey, preservePositioningInput);
                }
                return;
            }
            if (snappedObject.type === types_1.ITEM_TYPE.PANEL) {
                const drawDistancesToSnappedPanelEdges = (snappedObjectToPanel) => {
                    const dimensions = this.calculateDistancesToSnappedObjectEdges(snappedObjectToPanel);
                    if (dimensions && dimensions.length) {
                        // set longest distances to 0 to not show them
                        dimensions.forEach((dimension) => {
                            if (dimension.firstDimension <= dimension.secondDimension) {
                                // eslint-disable-next-line no-param-reassign
                                dimension.secondDimension = 0;
                            }
                            else {
                                // eslint-disable-next-line no-param-reassign
                                dimension.firstDimension = 0;
                            }
                        });
                        this.drawDistancesToSnappedObjectEdges(dimensions, activeLeaderKey, preservePositioningInput);
                    }
                };
                if (snappedObject.connectedItems.some((item) => item.itemType === types_1.ITEM_TYPE.EXTRUSION)) {
                    // leave only two shortest not collinear distances
                    const allDistancesToExtrusions = this.calculateDistancesToExtrusions(this.currentObject, activeLeaderKey);
                    const toShowDistances = [];
                    if (allDistancesToExtrusions === null || allDistancesToExtrusions === void 0 ? void 0 : allDistancesToExtrusions.length) {
                        allDistancesToExtrusions.sort((distance1, distance2) => distance1.distance - distance2.distance);
                        toShowDistances.push(allDistancesToExtrusions[0]);
                        const perpendicularDistances = allDistancesToExtrusions.filter((distance) => {
                            return !utils_1.Utils.areVectorsCollinear(distance.line.start.clone().sub(distance.line.end).normalize(), toShowDistances[0].line.start.clone().sub(toShowDistances[0].line.end).normalize());
                        });
                        if (perpendicularDistances.length) {
                            perpendicularDistances.sort((distance1, distance2) => distance1.distance - distance2.distance);
                            toShowDistances.push(perpendicularDistances[0]);
                        }
                        this.drawDistancesToExtrusions(toShowDistances, activeLeaderKey, preservePositioningInput);
                    }
                    else {
                        drawDistancesToSnappedPanelEdges(snappedObject);
                    }
                }
                else {
                    drawDistancesToSnappedPanelEdges(snappedObject);
                }
                return;
            }
        }
        if (this.currentObject.type !== types_1.ITEM_TYPE.EXTRUSION) {
            return;
        }
        const distancesToExtrusions = this.calculateDistancesToExtrusions(this.currentObject, activeLeaderKey);
        if (distancesToExtrusions) {
            this.drawDistancesToExtrusions(distancesToExtrusions, activeLeaderKey, preservePositioningInput);
        }
    }
    drawDistancesToSnappedObjectEdges(dimensions, activeLeaderKey, preservePositioningInput = false) {
        this.dispose();
        if (!(dimensions === null || dimensions === void 0 ? void 0 : dimensions.length)) {
            return;
        }
        dimensions.forEach((dimension) => {
            if (dimension.firstDimension === undefined ||
                dimension.secondDimension === undefined ||
                dimension.directionAxis === undefined ||
                dimension.firstDimension < -1e-4 ||
                dimension.secondDimension < -1e-4) {
                return;
            }
            const GUIDELINE_LENGTH = 1;
            const objectBox = utils_1.Utils.getAABBox(this.currentObject);
            const detectedObjectBox = utils_1.Utils.getAABBox(dimension.snappedObject);
            const objectCenter = new three_1.Vector3();
            objectBox.getCenter(objectCenter);
            const detectedObjectCenter = new three_1.Vector3();
            detectedObjectBox.getCenter(detectedObjectCenter);
            const leader1point1 = objectCenter.clone();
            const leader1point2 = objectCenter.clone();
            const leader2point1 = objectCenter.clone();
            const leader2point2 = objectCenter.clone();
            leader2point1[dimension.directionAxis] = detectedObjectBox.max[dimension.directionAxis];
            leader2point2[dimension.directionAxis] = objectBox.max[dimension.directionAxis];
            leader1point1[dimension.directionAxis] = objectBox.min[dimension.directionAxis];
            leader1point2[dimension.directionAxis] = detectedObjectBox.min[dimension.directionAxis];
            const objectAxis = utils_1.Utils.vecToAxis(this.currentObject.directionNormal);
            if (detectedObjectBox.max[objectAxis] < objectBox.max[objectAxis]) {
                const objAxisValue = detectedObjectBox.max[objectAxis] + 0.5;
                leader1point1[objectAxis] = objAxisValue;
                leader1point2[objectAxis] = objAxisValue;
                leader2point1[objectAxis] = objAxisValue;
                leader2point2[objectAxis] = objAxisValue;
            }
            else {
                const objAxisValue = detectedObjectBox.min[objectAxis] - 0.5;
                leader1point1[objectAxis] = objAxisValue;
                leader1point2[objectAxis] = objAxisValue;
                leader2point1[objectAxis] = objAxisValue;
                leader2point2[objectAxis] = objAxisValue;
            }
            const guidLineDirection = this.currentObject.directionNormal.clone().setLength(GUIDELINE_LENGTH);
            const firstLeaderKey = `${dimension.snappedObject.uuid}:${dimension.directionAxis}:positive`;
            const secondLeaderKey = `${dimension.snappedObject.uuid}:${dimension.directionAxis}:negative`;
            const isFirstLeaderActive = firstLeaderKey === activeLeaderKey;
            const isSecondLeaderActive = secondLeaderKey === activeLeaderKey;
            const shouldDrawFirst = Math.abs(dimension.firstDimension) > 1e-4 || isFirstLeaderActive;
            const shouldDrawSecond = Math.abs(dimension.secondDimension) > 1e-4 || isSecondLeaderActive;
            if (shouldDrawFirst) {
                const leader1 = this.createLeader();
                leader1.draw(leader1point1, leader1point2);
                this.addPositioningLeaderAlongAxis(leader1, firstLeaderKey, dimension.firstDimension, dimension.directionAxis, 1);
                this.toEndsLeaders.push(leader1);
                const guideline1point1 = this.currentObject.position.clone();
                const guideline1point2 = this.currentObject.position.clone().add(guidLineDirection);
                guideline1point1[dimension.directionAxis] = detectedObjectBox.min[dimension.directionAxis];
                guideline1point2[dimension.directionAxis] = detectedObjectBox.min[dimension.directionAxis];
                this.addGuideline(guideline1point1, guideline1point2);
            }
            if (shouldDrawSecond) {
                const leader2 = this.createLeader();
                leader2.draw(leader2point1, leader2point2);
                this.addPositioningLeaderAlongAxis(leader2, secondLeaderKey, dimension.secondDimension, dimension.directionAxis, -1);
                this.toEndsLeaders.push(leader2);
                const guideline2point1 = this.currentObject.position.clone();
                const guideline2point2 = this.currentObject.position.clone().add(guidLineDirection);
                guideline2point1[dimension.directionAxis] = detectedObjectBox.max[dimension.directionAxis];
                guideline2point2[dimension.directionAxis] = detectedObjectBox.max[dimension.directionAxis];
                this.addGuideline(guideline2point1, guideline2point2);
            }
        });
        this.showPositioningInput(activeLeaderKey, preservePositioningInput);
    }
    drawDistancesToExtrusions(distances, activeLeaderKey, preservePositioningInput = false) {
        this.dispose();
        distances.forEach((distance) => {
            const leader = this.createLeader();
            this.neighborLeaders.push(leader);
            leader.drawOnlyLine(distance.line.start, distance.line.end);
            leader.drawLabelByPositionAndDistance((0, utils_3d_1.getAABBoxCenter)(leader.getLine()), distance.distance);
            const position = this.currentObject.position.clone();
            this.addPositioningLeader(leader, distance.leaderKey, distance.distance, (requestedDistance) => {
                const direction = distance.line.end.clone().sub(distance.line.start).normalize();
                return position.clone().add(direction.multiplyScalar(distance.distance - requestedDistance));
            });
        });
        this.showPositioningInput(activeLeaderKey, preservePositioningInput);
    }
    definePanelDimensions(object) {
        this.dispose();
        if (object.type !== 'Panel') {
            return;
        }
        const DIST_TO_LEADER = 0.5;
        const GUIDELINE_LENGTH = 0.75;
        const panelBBox = utils_1.Utils.getAABBox(object);
        const horizontalLeaderPoint1 = panelBBox.min.clone();
        const horizontalLeaderPoint2 = panelBBox.min.clone();
        const verticalLeaderPoint1 = panelBBox.max.clone();
        const verticalLeaderPoint2 = panelBBox.max.clone();
        const bObject = object;
        if ((0, utils_3d_1.areNotEqual)(bObject.directionNormal.x, 0)) {
            horizontalLeaderPoint2.y = panelBBox.max.y;
            verticalLeaderPoint1.z = panelBBox.min.z;
            const x = (panelBBox.max.x + panelBBox.min.x) / 2;
            horizontalLeaderPoint1.x = x;
            horizontalLeaderPoint2.x = x;
            verticalLeaderPoint1.x = x;
            verticalLeaderPoint2.x = x;
            this.addGuideline(horizontalLeaderPoint1, new three_1.Vector3(horizontalLeaderPoint1.x, horizontalLeaderPoint1.y, horizontalLeaderPoint1.z - GUIDELINE_LENGTH));
            this.addGuideline(horizontalLeaderPoint2, new three_1.Vector3(horizontalLeaderPoint2.x, panelBBox.max.y, horizontalLeaderPoint2.z - GUIDELINE_LENGTH));
            this.addGuideline(verticalLeaderPoint1, new three_1.Vector3(verticalLeaderPoint1.x, verticalLeaderPoint1.y + GUIDELINE_LENGTH, panelBBox.min.z));
            this.addGuideline(verticalLeaderPoint2, new three_1.Vector3(verticalLeaderPoint2.x, verticalLeaderPoint2.y + GUIDELINE_LENGTH, verticalLeaderPoint2.z));
            horizontalLeaderPoint1.z -= DIST_TO_LEADER;
            horizontalLeaderPoint2.z -= DIST_TO_LEADER;
            verticalLeaderPoint1.y += DIST_TO_LEADER;
            verticalLeaderPoint2.y += DIST_TO_LEADER;
        }
        else if ((0, utils_3d_1.areNotEqual)(bObject.directionNormal.y, 0)) {
            horizontalLeaderPoint2.x = panelBBox.max.x;
            verticalLeaderPoint1.z = panelBBox.min.z;
            const y = (panelBBox.max.y + panelBBox.min.y) / 2;
            horizontalLeaderPoint1.y = y;
            horizontalLeaderPoint2.y = y;
            verticalLeaderPoint1.y = y;
            verticalLeaderPoint2.y = y;
            this.addGuideline(horizontalLeaderPoint1, new three_1.Vector3(horizontalLeaderPoint1.x, horizontalLeaderPoint1.y, horizontalLeaderPoint1.z - GUIDELINE_LENGTH));
            this.addGuideline(horizontalLeaderPoint2, new three_1.Vector3(panelBBox.max.x, horizontalLeaderPoint2.y, horizontalLeaderPoint2.z - GUIDELINE_LENGTH));
            this.addGuideline(verticalLeaderPoint1, new three_1.Vector3(verticalLeaderPoint1.x + GUIDELINE_LENGTH, verticalLeaderPoint1.y, panelBBox.min.z));
            this.addGuideline(verticalLeaderPoint2, new three_1.Vector3(verticalLeaderPoint2.x + GUIDELINE_LENGTH, verticalLeaderPoint2.y, verticalLeaderPoint2.z));
            horizontalLeaderPoint1.z -= DIST_TO_LEADER;
            horizontalLeaderPoint2.z -= DIST_TO_LEADER;
            verticalLeaderPoint1.x += DIST_TO_LEADER;
            verticalLeaderPoint2.x += DIST_TO_LEADER;
        }
        else if ((0, utils_3d_1.areNotEqual)(bObject.directionNormal.z, 0)) {
            horizontalLeaderPoint2.x = panelBBox.max.x;
            verticalLeaderPoint1.y = panelBBox.min.y;
            const z = (panelBBox.max.z + panelBBox.min.z) / 2;
            horizontalLeaderPoint1.z = z;
            horizontalLeaderPoint2.z = z;
            verticalLeaderPoint1.z = z;
            verticalLeaderPoint2.z = z;
            this.addGuideline(horizontalLeaderPoint1, new three_1.Vector3(horizontalLeaderPoint1.x, horizontalLeaderPoint1.y - GUIDELINE_LENGTH, horizontalLeaderPoint1.z));
            this.addGuideline(horizontalLeaderPoint2, new three_1.Vector3(panelBBox.max.x, horizontalLeaderPoint2.y - GUIDELINE_LENGTH, horizontalLeaderPoint2.z));
            this.addGuideline(verticalLeaderPoint1, new three_1.Vector3(verticalLeaderPoint1.x + GUIDELINE_LENGTH, panelBBox.min.y, verticalLeaderPoint1.z));
            this.addGuideline(verticalLeaderPoint2, new three_1.Vector3(verticalLeaderPoint2.x + GUIDELINE_LENGTH, verticalLeaderPoint2.y, verticalLeaderPoint2.z));
            horizontalLeaderPoint1.y -= DIST_TO_LEADER;
            horizontalLeaderPoint2.y -= DIST_TO_LEADER;
            verticalLeaderPoint1.x += DIST_TO_LEADER;
            verticalLeaderPoint2.x += DIST_TO_LEADER;
        }
        const horizontalLeader = this.createLeader();
        const verticalLeader = this.createLeader();
        this.panelLeaders.push(horizontalLeader, verticalLeader);
        horizontalLeader.draw(horizontalLeaderPoint1, horizontalLeaderPoint2);
        verticalLeader.draw(verticalLeaderPoint1, verticalLeaderPoint2);
    }
    updateLabelsPositions() {
        this.toEndsLeaders.forEach((leader) => leader.updateLabelPosition());
        this.neighborLeaders.forEach((leader) => leader.updateLabelPosition());
        this.panelLeaders.forEach((leader) => leader.updateLabelPosition());
        this.positionPositioningInput();
    }
    addPositioningLeader(leader, leaderKey, distance, getPosition) {
        if (!this.getPositioningPreview()) {
            return;
        }
        this.positioningLeaders.push({ leaderKey, leader, distance, getPosition });
    }
    addPositioningLeaderAlongAxis(leader, leaderKey, distance, axis, direction) {
        const position = this.currentObject.position.clone();
        this.addPositioningLeader(leader, leaderKey, distance, (requestedDistance) => {
            const nextPosition = position.clone();
            nextPosition[axis] += direction * (requestedDistance - distance);
            return nextPosition;
        });
    }
    getPositioningPreview() {
        const preview = this.snappingTool.getObject();
        if (!this.mainTool.draggingInProgress ||
            preview !== this.currentObject ||
            !preview.isMovable ||
            !preview.isSnappable) {
            return undefined;
        }
        if ((0, utils_3d_1.isExtrusion)(preview)) {
            return preview;
        }
        if (!(0, utils_3d_1.isComponent)(preview) ||
            !(0, utils_3d_1.isExtrusion)(this.snappingTool.neighborObject) ||
            this.snappingTool.neighborObject.scalingAngle !== 0) {
            return undefined;
        }
        return preview;
    }
    showPositioningInput(activeLeaderKey, preservePositioningInput = false) {
        const preview = this.getPositioningPreview();
        if (!preview || !this.positioningLeaders.length) {
            return;
        }
        if (this.positioningPreviewUUID !== preview.uuid) {
            this.positioningPreviewUUID = preview.uuid;
            this.positioningLeaderIndex = 0;
        }
        const leaderIndex = this.positioningLeaders.findIndex((leader) => leader.leaderKey === activeLeaderKey);
        if (leaderIndex >= 0) {
            this.positioningLeaderIndex = leaderIndex;
        }
        else {
            preservePositioningInput = false;
        }
        this.positioningLeaderIndex %= this.positioningLeaders.length;
        const positioningLeader = this.positioningLeaders[this.positioningLeaderIndex];
        this.positioningLeaders.forEach((leader) => leader.leader.setLabelVisible(true));
        positioningLeader.leader.setLabelVisible(false);
        this.positionPositioningInput();
        const value = this.viewer.isImperialMeasurementUsed
            ? positioningLeader.distance
            : positioningLeader.distance * consts_1.consts.INCH_TO_MM;
        document.body.dispatchEvent(new CustomEvent(events_1.positioningValueChanged, {
            detail: {
                value: String(Number(value.toFixed(3))),
                preserveValue: preservePositioningInput,
                onChange: (distance) => this.positionPreviewByDistance(distance),
                onNavigate: (reverse) => this.navigatePositioningInput(reverse),
                onConfirm: () => this.mainTool.confirmPositioningInput(),
            },
        }));
    }
    positionPositioningInput() {
        const positioningLeader = this.positioningLeaders[this.positioningLeaderIndex];
        const popup = document.getElementById(Cad3d_scss_1.default.lengthEditableLabel);
        if (!positioningLeader || !popup) {
            return;
        }
        const position = positioningLeader.leader.getLabelPosition();
        popup.style.left = position.left;
        popup.style.top = position.top;
        popup.style.display = 'block';
    }
    hidePositioningInput() {
        const popup = document.getElementById(Cad3d_scss_1.default.lengthEditableLabel);
        if (popup) {
            popup.style.display = 'none';
        }
    }
    navigatePositioningInput(reverse) {
        if (!this.positioningLeaders.length) {
            return;
        }
        this.positioningLeaderIndex =
            (this.positioningLeaderIndex + (reverse ? -1 : 1) + this.positioningLeaders.length) % this.positioningLeaders.length;
        this.showPositioningInput();
    }
    positionPreviewByDistance(requestedDistance) {
        const positioningLeader = this.positioningLeaders[this.positioningLeaderIndex];
        if (!positioningLeader) {
            return 'This distance can only be edited while placing an item.';
        }
        const error = this.snappingTool.tryPositionObject(positioningLeader.getPosition(requestedDistance));
        if (!error) {
            this.refreshPositioningDistances(positioningLeader.leaderKey);
        }
        return error;
    }
    calculateDistancesToSnappedObjectEdges(snappedObject) {
        var _a;
        if (!this.currentObject) {
            return undefined;
        }
        const connectedItems = this.currentObject.connectedItems.map((item) => this.viewer.getObjectById(item.uuid));
        const dimensions = [];
        if (snappedObject && Boolean(this.snappingTool.getObject())) {
            (_a = snappedObject.scalingAxes) === null || _a === void 0 ? void 0 : _a.forEach((axis) => {
                const distances = this.calculateSpecificDistancesToEnds(snappedObject, axis);
                if (distances) {
                    dimensions.push({
                        firstDimension: distances.firstDimension,
                        secondDimension: distances.secondDimension,
                        directionAxis: axis,
                        snappedObject,
                    });
                }
            });
        }
        else if (connectedItems && connectedItems.length) {
            connectedItems.forEach((item) => {
                var _a;
                (_a = item.scalingAxes) === null || _a === void 0 ? void 0 : _a.forEach((axis) => {
                    const distances = this.calculateSpecificDistancesToEnds(item, axis);
                    if (distances) {
                        dimensions.push({
                            firstDimension: distances.firstDimension,
                            secondDimension: distances.secondDimension,
                            directionAxis: axis,
                            snappedObject: item,
                        });
                    }
                });
            });
        }
        return dimensions;
    }
    getSnappedObject() {
        if (this.currentObject.connectedItems.length === 1) {
            return this.viewer.getObjectById(this.currentObject.connectedItems[0].uuid);
        }
        const { objectsIntersections } = this.viewer;
        const objectNeighbor = objectsIntersections.outerBox || objectsIntersections.backgroundPlane;
        if (!objectNeighbor) {
            return undefined;
        }
        return this.viewer.getObjectById(objectNeighbor.object.name.split('|')[1]);
    }
    calculateSpecificDistancesToEnds(item, axis) {
        if (!item) {
            return undefined;
        }
        if (item.type === types_1.ITEM_TYPE.EXTRUSION &&
            (0, utils_3d_1.areVectorsCollinear)(item.directionNormal, this.currentObject.directionNormal)) {
            return undefined;
        }
        const objectBBox = utils_1.Utils.getAABBox(this.currentObject);
        const neighborObjectBbox = utils_1.Utils.getAABBox(item);
        const firstDimension = objectBBox.min[axis] - neighborObjectBbox.min[axis];
        const secondDimension = neighborObjectBbox.max[axis] - objectBBox.max[axis];
        return {
            firstDimension,
            secondDimension,
        };
    }
    calculateDistancesToExtrusions(object = null, activeLeaderKey) {
        const sceneObjects = this.viewer
            .getScene()
            .children.filter((child) => child.type === 'Extrusion' && child.uuid !== object.uuid);
        if (sceneObjects.length === 0) {
            return undefined;
        }
        const objectBox = utils_1.Utils.getAABBox(object);
        const objectBoxProjections = {
            x: new three_1.Box3(),
            y: new three_1.Box3(),
            z: new three_1.Box3(),
        };
        const axes = [consts_2.Axis.x, consts_2.Axis.y, consts_2.Axis.z];
        axes.forEach((axis) => {
            this.projectAlongAxis(objectBox, axis, objectBoxProjections[axis]);
        });
        const result = {
            x: {
                positive: createNearestDistance(),
                negative: createNearestDistance(),
            },
            y: {
                positive: createNearestDistance(),
                negative: createNearestDistance(),
            },
            z: {
                positive: createNearestDistance(),
                negative: createNearestDistance(),
            },
        };
        const box3 = new three_1.Box3();
        const box3projection = new three_1.Box3();
        const objectIsDragging = this.mainTool.dragControls.isDraggingObject(object);
        sceneObjects.forEach((anotherObject) => {
            if (objectIsDragging && this.mainTool.dragControls.isDraggingObject(anotherObject)) {
                return;
            }
            box3.setFromObject(anotherObject.mesh);
            axes.forEach((axis) => {
                this.projectAlongAxis(box3, axis, box3projection);
                if (box3projection.intersectsBox(objectBoxProjections[axis])) {
                    let direction;
                    let objectCorner;
                    let anotherObjectCorner;
                    if (objectBox.max[axis] > box3.max[axis]) {
                        direction = 'negative';
                        objectCorner = objectBox.min.clone();
                        anotherObjectCorner = box3.max.clone();
                    }
                    else {
                        direction = 'positive';
                        objectCorner = objectBox.max.clone();
                        anotherObjectCorner = box3.min.clone();
                    }
                    const distance = Math.abs(objectCorner[axis] - anotherObjectCorner[axis]);
                    const leaderKey = `${anotherObject.uuid}:${axis}:${direction}`;
                    const isZeroDistance = (0, utils_3d_1.areEqual)(distance, 0);
                    const isActiveLeader = leaderKey === activeLeaderKey;
                    const isActiveZeroDistance = isZeroDistance && isActiveLeader;
                    const isInactiveZeroDistance = isZeroDistance && !isActiveLeader;
                    if (isInactiveZeroDistance) {
                        result[axis][direction].minDist = -Infinity;
                    }
                    else if (!isZeroDistance && objectBox.intersectsBox(box3)) {
                        return;
                    }
                    if (isActiveZeroDistance || distance < result[axis][direction].minDist) {
                        result[axis][direction].minDist = distance;
                        box3projection.intersect(objectBoxProjections[axis]);
                        const center3 = new three_1.Vector3();
                        box3projection.getCenter(center3);
                        const line = new three_1.Line3();
                        line.start.copy(center3);
                        line.end.copy(center3);
                        line.start[axis] = objectCorner[axis];
                        line.end[axis] = anotherObjectCorner[axis];
                        result[axis][direction].line = line;
                        result[axis][direction].leaderKey = leaderKey;
                    }
                }
            });
        });
        const visibleDirections = [];
        axes.forEach((axis) => {
            [result[axis].negative, result[axis].positive].forEach((nearestDistance) => {
                const isActiveLeader = nearestDistance.leaderKey === activeLeaderKey;
                const isVisibleDirection = nearestDistance.line && (nearestDistance.minDist > 0 || isActiveLeader);
                if (isVisibleDirection) {
                    visibleDirections.push(nearestDistance);
                }
            });
        });
        return visibleDirections.map((dir) => ({
            distance: dir.minDist,
            line: dir.line,
            leaderKey: dir.leaderKey,
        }));
    }
    addGuideline(startPoint, endPoint) {
        const points = [];
        points.push(startPoint);
        points.push(endPoint);
        const geometry = new three_1.BufferGeometry().setFromPoints(points);
        const material = new three_1.LineBasicMaterial({ color: consts_1.consts.WHITE_COLOR });
        const line = new three_1.Line(geometry, material);
        this.guidelines.push(line);
        this.viewer.getScene().add(line);
    }
    // eslint-disable-next-line class-methods-use-this
    createLineMaterial() {
        return new three_1.LineDashedMaterial({
            color: consts_1.consts.LEADER_COLOR,
            dashSize: consts_1.consts.DASH_LENGTH,
            gapSize: consts_1.consts.GAP_LENGTH,
        });
    }
    // eslint-disable-next-line class-methods-use-this
    createLeader() {
        return new DimensioningLeader_3d_1.default(this.viewer, this.lineMaterial, '');
    }
    // eslint-disable-next-line class-methods-use-this
    createOverlayLine(point1, point2) {
        const material = new three_1.LineBasicMaterial({
            color: consts_1.consts.TERRACOTTA_COLOR,
        });
        const geometry = new three_1.BufferGeometry().setFromPoints([point1, point2]);
        return new three_1.Line(geometry, material);
    }
    // eslint-disable-next-line class-methods-use-this
    projectAlongAxis(box3, axis, target) {
        const res = target !== null && target !== void 0 ? target : new three_1.Box3();
        res.copy(box3);
        res.min[axis] = 0;
        res.max[axis] = 0;
        return res;
    }
}
exports.default = DimensioningTool;
